import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { usePaymentProviderFactory } from '@pages/Traders-Room/Wallets/WalletDeposit/hooks/usePaymentProviderFactory'

import { BaseModalType } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Divider } from '@ui/components/Divider'
import { Modal } from '@ui/components/Modal'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { PaymentProvider } from '@model/PaymentProviderDto'
import type { Wallet } from '@model/WalletDepositFormValues'
import { WalletDto } from '@model/WalletDto'
import { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { PaymentProviderDepositModalOption } from '@shared/modals/PaymentProvider.modal'

interface PaymentProviderModalProps {
  onSelectOption(option: PaymentProvider, walletPaymentProvider: WalletPaymentProvider): void
  wallet?: Wallet | WalletDto
}

export const WalletDepositPaymentProviderModal: BaseModalType<PaymentProviderModalProps> = ({
  onSelectOption,
  close,
  wallet,
}) => {
  const { walletId } = useParams<{ walletId?: string }>()
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { createPaymentProvider } = usePaymentProviderFactory()
  const { isDefaultCFDProductType } = useProductReadContext()

  const [isSelectLoading, setSelectLoading] = useState<boolean>(false)
  const isProductTypeCFD = isDefaultCFDProductType()

  const resolvedWalletId = wallet?.id ?? walletId

  const { data: paymentProviders = [], isLoading } = useQuery({
    queryKey: queryKeys.deposit.methods('wallet', locale, resolvedWalletId),
    queryFn: () => apiClient.getWalletDepositMethods(locale, resolvedWalletId!),
    enabled: !!resolvedWalletId,
  })

  const handleSelectOption = async (walletPaymentProvider: WalletPaymentProvider) => {
    setSelectLoading(true)
    if (wallet?.currency) {
      const paymentProvider = await createPaymentProvider(walletPaymentProvider, wallet)
      onSelectOption(paymentProvider, walletPaymentProvider)
    }
  }

  const sortedProviders = React.useMemo(() => {
    if (!isProductTypeCFD) return paymentProviders
    const lastUsedProvider = paymentProviders.find((provider) => provider.lastDeposit)
    return lastUsedProvider
      ? [lastUsedProvider, ...paymentProviders.filter((provider) => provider !== lastUsedProvider)]
      : paymentProviders
  }, [isProductTypeCFD, paymentProviders])

  return (
    <Modal.FullScreen size='lg'>
      <Modal.Header>{t('Choose Payment Provider')}</Modal.Header>
      <Modal.Body isLoading={isLoading}>
        {sortedProviders.map((x, idx) => (
          <>
            <PaymentProviderDepositModalOption
              item={x}
              onSelectOption={handleSelectOption}
              key={x.method.id}
            />
            {idx < sortedProviders.length - 1 && <Divider.Horizontal />}
          </>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary loading={isSelectLoading} onClick={close}>
          {t('Close')}
        </Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}
